'use client';

import {
  CC_ROUNDUP_SEGMENT_PAGE_NAME,
  TrafficOriginType,
  type TrafficOriginTypeProps,
} from '@cc-shopping/contracts/credit-cards-page/constants';
import type { PreComputedFilterOfferIds } from '@cc-shopping/contracts/credit-cards-page/roundup/types';
import { ABSmartlyProvider } from '@cc-shopping/platform-absmartly/context/ABSmartlyProvider';
import type { ABSmartlyContextData } from '@cc-shopping/platform-absmartly/types/absmartly';
import type { ABTestVariantResult } from '@cc-shopping/platform-absmartly/utils/variants';
import { AnalyticsPageView as SegmentPageView } from '@cc-shopping/platform-analytics/AnalyticsPageView';
import { AcxiomDataCollection } from '@cc-shopping/sequencing/client/AcxiomDataCollection';
import { NerdWalletThemeProvider } from '@cc-shopping/ui/composites/NerdWalletThemeProvider';
import { CssBaseline } from '@mui/material';
import type { PropsWithChildren } from 'react';
import { ApprovalLikelihoodProvider } from 'src/features/approval-odds/context';
import { CompareBarProvider } from 'src/features/compare-bar/CompareBar/Context';
import type { ComparisonCreditCardOffer } from 'src/features/compare-bar/CompareBar/types';
import { WPAdminFeatures } from 'src/features/content/WPAdminFeatures';
import { FiltersDialogContextProvider } from 'src/features/roundup-filters/Mobile/components/FiltersDialogContext';
import { OffersFilterStoreProvider } from 'src/features/roundup-filters/store/Context';
import { CompassPageViewRegistration } from 'src/shared/analytics/CompassPageViewRegistration';

export type RoundupClientProvidersProps = {
  abSmartlyContextData?: ABSmartlyContextData;
  shouldEnableRoundupFilters?: boolean;
  staticExperimentResult: ABTestVariantResult;
  slug: string | undefined;
  id: string;
  preComputedFilterOfferIds: PreComputedFilterOfferIds;
  compareBarOffers: ComparisonCreditCardOffer[];
} & TrafficOriginTypeProps;

function TrafficOriginTypeToggledProviders({
  children,
  trafficOriginType,
}: PropsWithChildren<TrafficOriginTypeProps>) {
  if (trafficOriginType === TrafficOriginType.Paid) {
    return children;
  }
  return <ApprovalLikelihoodProvider>{children}</ApprovalLikelihoodProvider>;
}

export function RoundupClientProviders({
  abSmartlyContextData,
  children,
  compareBarOffers,
  id,
  preComputedFilterOfferIds,
  slug,
  shouldEnableRoundupFilters = false,
  staticExperimentResult,
  trafficOriginType,
}: PropsWithChildren<RoundupClientProvidersProps>) {
  const childrenProviders = (
    <CompareBarProvider allOffers={compareBarOffers}>
      <SegmentPageView
        name={CC_ROUNDUP_SEGMENT_PAGE_NAME[trafficOriginType]}
        contentId={id}
        contentSource="wp"
      >
        <CompassPageViewRegistration>
          <NerdWalletThemeProvider>
            <TrafficOriginTypeToggledProviders trafficOriginType={trafficOriginType}>
              <OffersFilterStoreProvider
                areFiltersEnabled={shouldEnableRoundupFilters}
                preComputedFilterOfferIds={preComputedFilterOfferIds}
              >
                <FiltersDialogContextProvider>
                  <AcxiomDataCollection slug={slug} />
                  <CssBaseline />
                  <WPAdminFeatures id={id} name="Roundup" />
                  {children}
                </FiltersDialogContextProvider>
              </OffersFilterStoreProvider>
            </TrafficOriginTypeToggledProviders>
          </NerdWalletThemeProvider>
        </CompassPageViewRegistration>
      </SegmentPageView>
    </CompareBarProvider>
  );

  return abSmartlyContextData ? (
    <ABSmartlyProvider
      contextData={abSmartlyContextData}
      staticExperimentResult={staticExperimentResult}
    >
      {childrenProviders}
    </ABSmartlyProvider>
  ) : (
    childrenProviders
  );
}
